import { CaretDown, CaretUp } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'

import BasicDetailsCard from '../../components/SalesforceAudit/common/BasicDetailsCard'
import ContextSwitcherTabs from '../../components/common/DDS/ContextSwitcherTab/ContextSwitcherTab'
import { DDSTypography } from 'den-design-system'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { PaginatedTable } from '../../components/SalesforceAudit/SFPaginatedTable'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import { getSalasForceUserPaginatedData } from '../../api/salesforceAudit/salesforceAudit.service'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { useParams } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFRoleDetails: React.FC = () => {
  const params = useParams()
  const store = useStoreContext()
  const { salesforceAuditStore } = useStoreContext()
  const [currentPage, setCurrentPage] = useState(1)
  const searchParams = new URLSearchParams(location.search)
  const data = salesforceAuditStore.getRoleById(params.id!)
  const [usersWithLicense, setUsersWithLicense] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId
  const [userTabIndex, setUserTabIndex] = useState<string>(
    searchParams.get('contextSwitcherTab') ?? '1'
  )
  const fetchSalesforcePaginatedUsers = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    try {
      const response: any = await getSalasForceUserPaginatedData({
        $salesforceAuditId: salesforceAuditId as string,
        page: currentPage,
        isActive: userTabIndex === '1' ? true : false,
        size: SalesforceAuditStore.userPageSize
      })

      if (response) {
        setUsersWithLicense(response.data)
        salesforceAuditStore.setUserLicensePaginatedData(response.data)
      }
    } catch (err) {
      console.error('Error fetching Salesforce users:', err)
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  useEffect(() => {
    if (salesforceAuditId && params.id) {
      fetchSalesforcePaginatedUsers()
    }
  }, [salesforceAuditId, params.id, userTabIndex, currentPage])

  const filteredUsers = useMemo(() => {
    const filtered = usersWithLicense.filter((user: any) => {
      return user?.record?.UserRole?.Id === params.id
    })

    return filtered
  }, [usersWithLicense, params.id, currentPage])

  const assignedUsersData = filteredUsers

  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return assignedUsersData

    return assignedUsersData.filter((item: any) => {
      const name = (item?.Name || '').toLowerCase()

      const profile = (item?.Profile?.Name || name).toLowerCase()

      // Prepare flexible date formats for LastLoginDate
      let formattedDates: string[] = []
      if (item?.LastLoginDate) {
        const m = moment(item.LastLoginDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('MMMM').toLowerCase(),
          m.format('D').toLowerCase()
        ]
      }

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)

        case 'CreatedAtDate':
          return formattedDates.some((dateFormat) =>
            dateFormat.includes(trimmedSearch)
          )

        case 'Profile':
          return profile.includes(trimmedSearch)

        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            profile.includes(trimmedSearch) ||
            formattedDates.some((dateFormat) =>
              dateFormat.includes(trimmedSearch)
            )
          )
      }
    })
  }, [assignedUsersData, activeSearchTerm, searchColumn])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData

    return [...filteredData].sort((a: any, b: any) => {
      let valA: any
      let valB: any

      // Handle special/nested keys manually
      if (sortConfig.key === 'Profile') {
        valA = a?.Profile?.Name?.toLowerCase?.() || ''
        valB = b?.Profile?.Name?.toLowerCase?.() || ''
      } else if (sortConfig.key === 'CreatedAtDate') {
        const dateA = a.LastLoginDate ? new Date(a.LastLoginDate).getTime() : 0
        const dateB = b.LastLoginDate ? new Date(b.LastLoginDate).getTime() : 0
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA
      } else {
        valA = a?.[sortConfig.key!] || ''
        valB = b?.[sortConfig.key!] || ''
      }

      // Numeric comparison
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }

      // String comparison
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }

      return 0
    })
  }, [filteredData, sortConfig, salesforceAuditStore])

  const userPaginatedData = sortedData.map((item: any) => item?.record)

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      },
      {
        label: 'Roles',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/roles',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      },
      {
        label: data?.Name,
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/roles',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])

  const Columns = [
    {
      columnHeaderStyle: { width: '500px' },
      columnDataStyle: { width: '500px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              User Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => {
        return (
          <div className='flex justify-start max-w-[180px] text-start row items-start'>
            <div>
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark4}
                className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
              >
                {text || text === 0 ? text : '-'}
              </DDSTypography.Paragraph>
            </div>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Created At
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('CreatedAtDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedAtDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedAtDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'CreatedAtDate',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              {moment(text).format(timeFormats.shortMonthDateYearTime)}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Profile
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Profile')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Profile',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text?.Name}
          </DDSTypography.Paragraph>
        </div>
      )
    }
  ]

  const ContextSwitcherTab = [
    {
      id: '1',
      label: 'Active Users',
      children: (
        <PaginatedTable
          data={userPaginatedData}
          columns={Columns}
          currentPage={currentPage}
          rowsPerPage={SalesforceAuditStore.userPageSize}
          totalCount={userPaginatedData?.length}
          onPageChange={(page) => setCurrentPage(page)}
          searchTerm={searchTerm}
          noDataText='No Roles defined at the moment.'
          noSearchMatchText='No Roles found for the given search.'
          noDataImage={NoRecordData}
          noSearchImage={NoRecordImage}
        />
      )
    },
    {
      id: '2',
      label: 'Inactive Users',
      children: (
        <PaginatedTable
          data={userPaginatedData}
          columns={Columns}
          currentPage={currentPage}
          rowsPerPage={SalesforceAuditStore.userPageSize}
          totalCount={userPaginatedData?.length}
          onPageChange={(page) => setCurrentPage(page)}
          searchTerm={searchTerm}
          noDataText='No Roles defined at the moment.'
          noSearchMatchText='No Roles found for the given search.'
          noDataImage={NoRecordData}
          noSearchImage={NoRecordImage}
        />
      )
    }
  ]

  return (
    <div className='flex flex-col w-full py-[24px]'>
      <div className='flex flex-col pb-[12px]'>
        <DDSTypography.Title
          type='h5'
          variant='semiBold'
          color={pallete.colors.textDark3}
        >
          Basic Details
        </DDSTypography.Title>
      </div>
      <div className='grid grid-cols-4 gap-[16px] pb-[24px]'>
        <BasicDetailsCard
          id={data?.id}
          heading='Parent Role'
          content={
            salesforceAuditStore.getRoleById(data?.ParentRoleId)?.Name ?? '-'
          }
        />
        <BasicDetailsCard
          id={data?.id}
          heading='Description'
          content={data?.RollupDescription}
        />
        <BasicDetailsCard
          id={data?.id}
          heading='Modified By'
          content={data?.LastModifiedBy?.Name}
        />
        <BasicDetailsCard
          id={data?.id}
          heading='Modified on'
          content={moment(data?.LastModifiedDate).format(
            timeFormats.shortMonthDateYearTime
          )}
        />
      </div>
      <div>
        {loading ? (
          <div>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className='flex flex-col'>
              <DDSTypography.Title
                type='h5'
                variant='semiBold'
                color={pallete.colors.textDark3}
              >
                {`Users Assigned (${assignedUsersData?.length?.toLocaleString('en-US')})`}
              </DDSTypography.Title>
            </div>
            <div className='flex flex-col w-full pt-[12px] pb-[24px]'>
              <div className='relative pb-[12px]'>
                <Search
                  id='search'
                  name='Search'
                  placeholder='Search'
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value
                    setSearchTerm(value)
                    setShowDropdown(value.length > 0)
                    if (value.trim() === '') {
                      setActiveSearchTerm('')
                    }
                  }}
                  autoComplete='off'
                  width='100%'
                  onPressEnter={() => {
                    setActiveSearchTerm(searchTerm.trim())
                    setCurrentPage(1)
                    setShowDropdown(false)
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowDropdown(false)
                      setCurrentPage(1)
                    }, 200)
                  }}
                />

                {showDropdown && (
                  <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                    {[
                      { label: 'Name', value: 'Name' },
                      { label: 'Created At', value: 'CreatedAtDate' },
                      { label: 'Profile', value: 'Profile' },
                      { label: 'All Columns', value: 'all' }
                    ].map((option) => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setSearchColumn(option.value)
                          setCurrentPage(1)
                          setActiveSearchTerm(searchTerm)
                          setShowDropdown(false)
                        }}
                        className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                      >
                        <span className='font-mulish text-black'>
                          {searchTerm}
                        </span>
                        <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                          in: {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className='  flex items-start w-full'>
                <div className='flex-grow '>
                  <ContextSwitcherTabs
                    tabOptions={ContextSwitcherTab}
                    shape='roundedRectangle'
                    tabStyle={{
                      border: '1px solid',
                      borderColor: pallete.colors.stroke2,
                      userSelect: 'none',
                      width: '300px'
                    }}
                    tabTitleClassName='text-center select-none'
                    activeTab={userTabIndex}
                    onChange={(index: any) => {
                      setUserTabIndex(index)
                    }}
                    disabled={loading || store.uiStore.getGlobalLoader()}
                    className='override-height override-view-more-tabs'
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
export default observer(SFRoleDetails)
